import { compareTasksByProject } from '@awork/features/task/functions/task-sorting'
import { TaskList } from '@awork/features/task/models/task-list.model'
import type { Task } from '@awork/features/task/models/task.model'
import { sortByAsc, sortByDesc } from '@awork/_shared/functions/lodash'
import type { User } from '@awork/features/user/models/user.model'
import type { Project } from '@awork/features/project/models/project.model'
import type { ProjectType } from '@awork/features/project/models/project-type.model'
import type { Company } from '@awork/features/company/models/company.model'
import { GroupByOptions } from '@awork/_shared/models/group-by-options.model'
import type { WorkType } from '@awork/features/task/models/work-type.model'
import { getSortedTasksByUser } from '@awork/features/task/functions/task-grouping'
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfToday,
  endOfTomorrow,
  endOfWeek,
  endOfYesterday,
  isAfter
} from '@awork/_shared/functions/date-fns-wrappers'
import type { SortByOption } from '@awork/_shared/models/sort-by-option.model'
import { taskSearchFilter } from '@awork/features/task/functions/task-query-helpers'
import { WITHOUT_LIST_ID } from '@awork/features/task/models/task-list.model.base'
import { TaskListTimeline } from '@awork/features/task/models/task-list-timeline.model'
import { isBefore } from 'date-fns'
import type {
  FlattenItem,
  FlattenItemsData,
  SubtasksByParentMap,
  ViewOptions,
  GenericGroup,
  GroupingEntity,
  GroupedTask
} from './flatten-tasks.types'
import {
  UpcomingGroup,
  UNGROUPED_TASKS_ID,
  PRIVATE_TASKS_ID,
  SEARCH_RESULTS_ID,
  SPACE_ITEM_ID,
  ADD_TASK_ID
} from './flatten-tasks.types'
import { SortByOptions } from '@awork/framework/state/settings.types'

type ClusterDueDateMap = Map<string, Date | null>

const dateGroups = [
  UpcomingGroup.TODAY,
  UpcomingGroup.TOMORROW,
  UpcomingGroup.NEXT_7_DAYS,
  UpcomingGroup.NEXT_30_DAYS,
  UpcomingGroup.THIS_WEEK,
  UpcomingGroup.NEXT_WEEK,
  UpcomingGroup.THIS_MONTH,
  UpcomingGroup.NEXT_MONTH,
  UpcomingGroup.RECENTLY_CLOSED,
  UpcomingGroup.UPCOMING,
  UpcomingGroup.UNSCHEDULED
]

const withoutList = TaskList.getWithoutList()
const searchGroup = { id: SEARCH_RESULTS_ID, name: SEARCH_RESULTS_ID }

/**
 * Sort function for grouped tasks that handles both WITHOUT_LIST_ID and regular list cases
 * For WITHOUT_LIST_ID: sorts by createdOn first, then by name if createdOn is the same
 * For regular lists: sorts by orderOfTask
 * @param {GroupedTask} a
 * @param {GroupedTask} b
 * @returns {number}
 */
export const sortGroupedTasksFn = (a: GroupedTask, b: GroupedTask): number => {
  if (a.group.id === WITHOUT_LIST_ID && b.group.id === WITHOUT_LIST_ID) {
    const aCreatedOn = a.task.createdOn ? getNormalizedTimeForDate(a.task.createdOn) : 0
    const bCreatedOn = b.task.createdOn ? getNormalizedTimeForDate(b.task.createdOn) : 0

    if (aCreatedOn !== bCreatedOn) {
      return aCreatedOn - bCreatedOn
    }

    return (a.task.name || '').localeCompare(b.task.name || '')
  }

  // For tasks with lists, sort by orderOfTask
  const aOrder = (a.group as TaskList).orderOfTask || 0
  const bOrder = (b.group as TaskList).orderOfTask || 0

  return aOrder - bOrder
}

/**
 * Returns a normalized timestamp with milliseconds set to 0
 * @param {string | Date} taskDate The date to normalize
 * @returns {number} Timestamp in milliseconds with milliseconds component set to 0
 */
function getNormalizedTimeForDate(taskDate: string | Date): number {
  const newDate = new Date(taskDate)

  newDate.setMilliseconds(0)

  return newDate.getTime()
}

/**
 * Returns a flattened array ordered and sorted by user view settings
 * @param {Task[]} tasks
 * @param {ViewOptions} [options={}]
 * @param {boolean} isInTimeline
 * @returns {FlattenItemsData}
 */
export function getFlattenItemsByGrouping(
  tasks: Task[],
  options: ViewOptions = {},
  isInTimeline?: boolean
): FlattenItemsData {
  const { groupOption, taskLists } = options

  switch (groupOption) {
    case GroupByOptions.upcomingDaily:
    case GroupByOptions.upcomingWeekly:
    case GroupByOptions.upcomingMonthly:
      return generateUpcomingLists(tasks, options)
    case GroupByOptions.taskLists:
      return isInTimeline
        ? flattenTasksByTimelineLists(tasks, taskLists, options)
        : flattenTasksByLists(tasks, taskLists, options)
    case GroupByOptions.project:
    case GroupByOptions.projectType:
      return generatePrivateAndProjectList(tasks, options)
    case GroupByOptions.typeOfWork:
      return generateWorkTypeList(tasks, options)
    case GroupByOptions.user:
      return generateUserList(tasks, options)
    case GroupByOptions.client:
      return generateClientList(tasks, options)
    default:
      return generateAllTasksItems(tasks, options)
  }
}

/**
 * Returns a flattened array ordered by timeline lists, tasks and subtasks
 * @param {Task[]} tasks
 * @param {TaskList[]} taskLists
 * @param {ViewOptions} [options={}]
 * @returns {FlattenItemsData}
 */
export function flattenTasksByTimelineLists(
  tasks: Task[],
  taskLists: TaskList[],
  options: ViewOptions = {}
): FlattenItemsData {
  const visibleListIds = new Set(taskLists?.map(list => list.id))
  const selectListsFn = getTaskListSelectFn(options, visibleListIds)

  const defaultTimelineListMap = new Map<string, TaskListTimeline>(
    taskLists.map(list => [list.id, new TaskListTimeline({ ...list, startOn: null, endOn: null })])
  )

  const { subtasksByParentMap, topLevelTasksByGroupMap, timelineListsMap } = getTasksMaps(
    tasks,
    selectListsFn,
    options,
    defaultTimelineListMap
  )
  const { isListCollapsed, isTaskCollapsed } = options

  const items: FlattenItem[] = []
  const tasksByGroupMap = new Map<string, Task[]>()
  const isSearching = !!options.searchString?.trim()

  const allLists = options.searchString ? [searchGroup] : [...timelineListsMap.values()]

  allLists.forEach(list => {
    const topLevelTasksInList = topLevelTasksByGroupMap.get(list.id) || []
    const topLevelTasks = topLevelTasksInList.sort(sortGroupedTasksFn)

    items.push({
      id: list.id,
      groupId: list.id,
      entity: list,
      type: 'group'
    })

    const isTaskListCollapsed = options.areListsCollapsible && isListCollapsed?.(list.id)

    if (options.allowTaskCreation && !isTaskListCollapsed && !isSearching) {
      items.push({
        id: ADD_TASK_ID,
        type: 'new',
        entity: null,
        group: taskLists?.find(taskList => taskList.id === list.id)
      })
    }

    tasksByGroupMap.set(list.id, [])

    topLevelTasks.forEach(({ task, group }, index) => {
      const subtasks = subtasksByParentMap.get(task.id)?.subtasks || []
      const hasVisibleSubtasks = subtasks?.length && !isTaskCollapsed?.(task.id)

      // Add subtasks to "allTasksByList"
      tasksByGroupMap.get(group.id).push(task, ...subtasks)

      if (isTaskListCollapsed) {
        return
      }

      task.currentListId = list.id

      items.push({
        id: task.id,
        entity: task,
        type: 'task',
        groupId: list.id,
        group: group as TaskList,
        hasSubtasks: !!subtasks?.length,
        isFirstInGroup: index === 0,
        isLastInGroup: !hasVisibleSubtasks && index === topLevelTasks.length - 1,
        isLastTopLevelTask: index === topLevelTasks.length - 1
      })

      if (hasVisibleSubtasks) {
        sortByAsc(subtasks, task => task.subtaskOrder).forEach((subtask, i) => {
          const isLastSubtask = i === subtasks.length - 1

          subtask.currentListId = list.id

          items.push({
            id: subtask.id,
            entity: subtask,
            type: 'subTask',
            groupId: list.id,
            group: list,
            parentTaskId: task.id,
            isFirstSubtask: i === 0,
            isLastSubtask: isLastSubtask,
            isLastInGroup: isLastSubtask && index === topLevelTasks.length - 1
          })
        })
      }
    })

    if (list instanceof TaskListTimeline) {
      list.taskIds = [...new Set(tasksByGroupMap.get(list.id).map(task => task.id))]
    }

    if (options.spaceBetweenGroups) {
      items.push({
        id: SPACE_ITEM_ID,
        type: 'space',
        entity: null
      })
    }
  })

  return { flattenItems: items, subtasksByParentMap, tasksByGroupMap }
}

/**
 * Returns a flattened array ordered by lists, tasks and subtasks
 * @param {Task[]} tasks
 * @param {TaskList[]} taskLists
 * @param {ViewOptions} [options={}]
 * @returns {FlattenItemsData}
 */
export function flattenTasksByLists(tasks: Task[], taskLists: TaskList[], options: ViewOptions = {}): FlattenItemsData {
  const selectListsFn = getTaskListSelectFn(options)

  const { subtasksByParentMap, topLevelTasksByGroupMap, clusterDueDateMap } = getTasksMaps(
    tasks,
    selectListsFn,
    options
  )
  const { isListCollapsed, isTaskCollapsed } = options

  const items: FlattenItem[] = []
  const tasksByGroupMap = new Map<string, Task[]>()
  const isSearching = !!options.searchString?.trim()

  let allLists = options.searchString ? [searchGroup] : [...(taskLists || [])]

  const tasksWithoutList = topLevelTasksByGroupMap.get(WITHOUT_LIST_ID)

  if (tasksWithoutList?.length && !options.searchString) {
    allLists = [...allLists, withoutList]
  }

  allLists.forEach(list => {
    const topLevelTasksInList = topLevelTasksByGroupMap.get(list.id) || []
    const topLevelTasks = topLevelTasksInList.sort(sortGroupedTasksFn)

    if (options.sortingOption?.value && options.sortingOption.value !== SortByOptions.default) {
      const sortedTasks = sortTasks(
        topLevelTasks.map(item => item.task),
        options.sortingOption,
        clusterDueDateMap
      )
      const order = new Map(sortedTasks.map((task, index) => [task.id, index]))

      topLevelTasks.sort((a, b) => order.get(a.task.id)! - order.get(b.task.id)!)
    }

    items.push({
      id: list.id,
      groupId: list.id,
      entity: list,
      type: 'group'
    })

    const isTaskListCollapsed = options.areListsCollapsible && isListCollapsed?.(list.id)

    if (options.allowTaskCreation && !isTaskListCollapsed && !isSearching) {
      items.push({
        id: ADD_TASK_ID,
        type: 'new',
        entity: null,
        group: taskLists?.find(taskList => taskList.id === list.id)
      })
    }

    tasksByGroupMap.set(list.id, [])

    topLevelTasks.forEach(({ task, group }, index) => {
      const subtasks = subtasksByParentMap.get(task.id)?.subtasks || []
      const hasVisibleSubtasks = subtasks?.length && !isTaskCollapsed?.(task.id)

      // Add subtasks to "allTasksByList"
      tasksByGroupMap.get(group.id).push(task, ...subtasks)

      if (isTaskListCollapsed) {
        return
      }

      task.currentListId = list.id

      items.push({
        id: task.id,
        entity: task,
        type: 'task',
        groupId: list.id,
        group: group as TaskList,
        hasSubtasks: !!subtasks?.length,
        isFirstInGroup: index === 0,
        isLastInGroup: !hasVisibleSubtasks && index === topLevelTasks.length - 1,
        isLastTopLevelTask: index === topLevelTasks.length - 1
      })

      if (hasVisibleSubtasks) {
        sortByAsc(subtasks, task => task.subtaskOrder).forEach((subtask, i) => {
          const isLastSubtask = i === subtasks.length - 1

          subtask.currentListId = list.id

          items.push({
            id: subtask.id,
            entity: subtask,
            type: 'subTask',
            groupId: list.id,
            group: list,
            parentTaskId: task.id,
            isFirstSubtask: i === 0,
            isLastSubtask: isLastSubtask,
            isLastInGroup: isLastSubtask && index === topLevelTasks.length - 1
          })
        })
      }
    })

    if (options.spaceBetweenGroups) {
      items.push({
        id: SPACE_ITEM_ID,
        type: 'space',
        entity: null
      })
    }
  })

  return { flattenItems: items, subtasksByParentMap, tasksByGroupMap }
}

/**
 * Returns a function that determines in which groups a given task should be
 * @param {ViewOptions} [options={}]
 * @param {Set<string>} visibleListIds
 * @returns {(task: Task) => TaskList[] | GenericGroup[]}
 */
function getTaskListSelectFn(
  options: ViewOptions = {},
  visibleListIds?: Set<string>
): (task: Task) => TaskList[] | GenericGroup[] {
  return task => {
    if (options.searchString) {
      return [searchGroup]
    }

    if (task.lists?.length) {
      const lists = visibleListIds ? task.lists.filter(list => visibleListIds.has(list.id)) : task.lists

      if (options.showTasksOnlyInPrimaryList && task.primaryTaskListId) {
        const primaryList = task.primaryTaskList

        if (primaryList && (!visibleListIds || visibleListIds.has(primaryList.id))) {
          return [primaryList]
        }

        return lists
      }

      return lists
    }

    return [withoutList]
  }
}

/**
 * Generate Task view based on User lists
 * @param {Task[]} tasks
 * @param {ViewOptions} [options={}]
 * @returns {FlattenItemsData}
 */
function generateUserList(tasks: Task[], options: ViewOptions = {}): FlattenItemsData {
  const selectListsFn = task => task.assignees

  const { subtasksByParentMap, topLevelTasksByGroupMap, clusterDueDateMap } = getTasksMaps(
    tasks,
    selectListsFn,
    options
  )

  const groups: User[] = []
  const tasksByGroupMap = new Map<string, Task[]>()

  const unassignedTasks = topLevelTasksByGroupMap.get(UNGROUPED_TASKS_ID) || []

  // Get all users from tasks
  for (const [key, groupedTask] of topLevelTasksByGroupMap) {
    if (key !== UNGROUPED_TASKS_ID) {
      groups.push(groupedTask[0].group as User)
    }
  }

  const sortedUserGroups = groups.sort(sortGroupsByNameFn)

  if (unassignedTasks.length) {
    sortedUserGroups.push({ id: UNGROUPED_TASKS_ID } as User)
  }

  const flattenItems =
    sortedUserGroups?.reduce((items: FlattenItem[], group: User) => {
      const groupId = group?.id || UNGROUPED_TASKS_ID

      const groupedTasks = topLevelTasksByGroupMap.get(groupId) || []

      if (!groupedTasks.length) {
        return items
      }

      items.push({
        id: groupId,
        groupId,
        entity: group,
        type: 'group'
      })

      const topLevelTasksInGroup = groupedTasks.map(groupedTask => groupedTask.task)

      const sortedTasks =
        group && options?.sortingOption?.value
          ? sortTasks(topLevelTasksInGroup, options.sortingOption, clusterDueDateMap)
          : topLevelTasksInGroup.sort(sortByCreatedOnFn)

      const { flattenItems, tasksInGroup } = generateFlattenItemsForGroup({
        sortedTasks,
        subtasksByParentMap,
        group,
        options,
        clusterDueDateMap
      })

      tasksByGroupMap.set(groupId, tasksInGroup)

      return items.concat(flattenItems)
    }, []) || []

  return { flattenItems, subtasksByParentMap, tasksByGroupMap }
}

/**
 * Generate Work types Task Lists
 * @param {Task[]} tasks
 * @param {ViewOptions} [options={}]
 * @returns {FlattenItemsData}
 */
function generateWorkTypeList(tasks: Task[], options: ViewOptions = {}): FlattenItemsData {
  const selectListsFn = (task: Task) => (task.typeOfWork ? [task.typeOfWork] : [])

  const { subtasksByParentMap, topLevelTasksByGroupMap, clusterDueDateMap } = getTasksMaps(
    tasks,
    selectListsFn,
    options
  )

  const groups: WorkType[] = []
  const tasksByGroupMap = new Map<string, Task[]>()

  // Get all users from tasks
  for (const [key, groupedTask] of topLevelTasksByGroupMap) {
    if (key !== UNGROUPED_TASKS_ID) {
      groups.push(groupedTask[0].group as WorkType)
    }
  }

  const sortedUserGroups = groups.sort(sortGroupsByNameFn)

  const flattenItems =
    sortedUserGroups?.reduce((items: FlattenItem[], group: WorkType) => {
      const groupId = group?.id || UNGROUPED_TASKS_ID
      const groupedTasks = topLevelTasksByGroupMap.get(groupId) || []

      if (!groupedTasks.length) {
        return items
      }

      items.push({
        id: groupId,
        groupId,
        entity: group,
        type: 'group'
      })

      const topLevelTasksInGroup = groupedTasks.map(groupedTask => groupedTask.task)

      const sortedTasks =
        group && options?.sortingOption?.value
          ? sortTasks(topLevelTasksInGroup, options.sortingOption, clusterDueDateMap)
          : topLevelTasksInGroup.sort(sortByCreatedOnFn)

      const { flattenItems, tasksInGroup } = generateFlattenItemsForGroup({
        sortedTasks,
        subtasksByParentMap,
        group,
        options,
        clusterDueDateMap
      })

      tasksByGroupMap.set(groupId, tasksInGroup)

      return items.concat(flattenItems)
    }, []) || []

  return { flattenItems, subtasksByParentMap, tasksByGroupMap }
}

/**
 * Generates client list task grouping
 * @param {Task[]} tasks
 * @param {ViewOptions} [options={}]
 * @returns {FlattenItemsData}
 */
function generateClientList(tasks: Task[], options: ViewOptions = {}): FlattenItemsData {
  const selectListsFn = (task: Task) => (task.project?.company ? [task.project.company] : [])

  const { subtasksByParentMap, topLevelTasksByGroupMap, clusterDueDateMap } = getTasksMaps(
    tasks,
    selectListsFn,
    options
  )

  const groups: Company[] = []
  const tasksByGroupMap = new Map<string, Task[]>()

  const nonClientTasks = topLevelTasksByGroupMap.get(UNGROUPED_TASKS_ID) || []

  // Get all users from tasks
  for (const [key, groupedTask] of topLevelTasksByGroupMap) {
    if (key !== UNGROUPED_TASKS_ID) {
      groups.push(groupedTask[0].group as Company)
    }
  }

  const sortedUserGroups = groups.sort(sortGroupsByNameFn)

  if (nonClientTasks.length) {
    sortedUserGroups.push({ id: UNGROUPED_TASKS_ID } as Company)
  }

  const flattenItems =
    sortedUserGroups?.reduce((items: FlattenItem[], group: Company) => {
      const groupId = group?.id || UNGROUPED_TASKS_ID

      const groupedTasks = topLevelTasksByGroupMap.get(groupId) || []

      if (!groupedTasks.length) {
        return items
      }

      items.push({
        id: groupId,
        groupId,
        entity: group,
        type: 'group'
      })

      const topLevelTasksInGroup = groupedTasks.map(groupedTask => groupedTask.task)

      const sortedTasks =
        group && options?.sortingOption?.value
          ? sortTasks(topLevelTasksInGroup, options.sortingOption, clusterDueDateMap)
          : topLevelTasksInGroup.sort(sortByCreatedOnFn)

      const { flattenItems, tasksInGroup } = generateFlattenItemsForGroup({
        sortedTasks,
        subtasksByParentMap,
        group,
        options,
        clusterDueDateMap
      })

      tasksByGroupMap.set(groupId, tasksInGroup)

      return items.concat(flattenItems)
    }, []) || []

  return { flattenItems, subtasksByParentMap, tasksByGroupMap }
}

/**
 * Generate Upcoming task lists based on due on
 * @param {Task[]} tasks
 * @param {ViewOptions} [options={}]
 * @returns {FlattenItemsData}
 */
function generateUpcomingLists(tasks: Task[], options: ViewOptions = {}): FlattenItemsData {
  // Precompute subtasks map so we can derive cluster due dates for grouping
  const precomputed = getTasksMaps(tasks, () => [], options, undefined, true)
  const clusterDueDateMap = precomputed.clusterDueDateMap
  const selectListsFn = getUpcomingGroupFn(options?.groupOption, clusterDueDateMap)

  const { subtasksByParentMap, topLevelTasksByGroupMap } = getTasksMaps(tasks, selectListsFn, options)

  const tasksByGroupMap = new Map<string, Task[]>()

  const flattenItems =
    dateGroups?.reduce((items: FlattenItem[], dateGroupId: string) => {
      const groupedTasks = topLevelTasksByGroupMap.get(dateGroupId) || []

      if (!groupedTasks.length) {
        return items
      }

      items.push({
        id: dateGroupId,
        groupId: dateGroupId,
        entity: { id: dateGroupId, name: dateGroupId },
        type: 'group'
      })

      const topLevelTasksInGroup = groupedTasks.map(groupedTask => groupedTask.task)

      const sortedTasks = options?.sortingOption?.value
        ? sortTasks(topLevelTasksInGroup, options.sortingOption, clusterDueDateMap)
        : topLevelTasksInGroup.sort(sortByCreatedOnFn)

      const { flattenItems, tasksInGroup } = generateFlattenItemsForGroup({
        sortedTasks,
        subtasksByParentMap,
        group: { id: dateGroupId, name: dateGroupId },
        options,
        clusterDueDateMap
      })

      tasksByGroupMap.set(dateGroupId, tasksInGroup)

      return items.concat(flattenItems)
    }, []) || []

  return { flattenItems, subtasksByParentMap, tasksByGroupMap }
}

/**
 * Generate Private and Project Task Lists
 * @param {Task[]} tasks
 * @param {ViewOptions} [options={}]
 * @returns {FlattenItemsData}
 */
function generatePrivateAndProjectList(tasks: Task[], options: ViewOptions = {}): FlattenItemsData {
  const selectListsFn = getProjectsGroupFn(options.groupOption)

  const { subtasksByParentMap, topLevelTasksByGroupMap, clusterDueDateMap } = getTasksMaps(
    tasks,
    selectListsFn,
    options
  )
  let groups: Project[] = []

  const tasksByGroupMap = new Map<string, Task[]>()
  const ungroupedTasks = topLevelTasksByGroupMap.get(UNGROUPED_TASKS_ID)
  const privateTasks = topLevelTasksByGroupMap.get(PRIVATE_TASKS_ID)

  // Get all users from tasks
  for (const [key, groupedTask] of topLevelTasksByGroupMap) {
    if (key !== UNGROUPED_TASKS_ID && key !== PRIVATE_TASKS_ID) {
      groups.push(groupedTask[0].group as Project)
    }
  }

  groups = groups.sort(sortGroupsByNameFn)

  if (privateTasks?.length) {
    groups.unshift({ id: PRIVATE_TASKS_ID } as Project)
  }

  if (ungroupedTasks?.length) {
    groups.push({ id: UNGROUPED_TASKS_ID } as Project)
  }

  const flattenItems =
    groups?.reduce((items: FlattenItem[], group: Project) => {
      const groupId = group?.id || UNGROUPED_TASKS_ID
      const isPrivateTasksGroup = groupId === PRIVATE_TASKS_ID
      const allowTaskCreation = options.allowTaskCreation && options.canEditProjectTasksFn?.(group)
      const isSearching = !!options.searchString?.trim()

      const groupedTasks = topLevelTasksByGroupMap.get(groupId) || []

      if (!groupedTasks.length && !isPrivateTasksGroup) {
        return items
      }

      items.push({
        id: groupId,
        groupId,
        entity: group,
        type: 'group'
      })

      if (allowTaskCreation && !isSearching) {
        items.push({
          group,
          id: ADD_TASK_ID,
          type: 'new',
          entity: null
        })
      }

      const topLevelTasksInGroup = groupedTasks.map(groupedTask => groupedTask.task)

      const sortedTasks =
        isPrivateTasksGroup && !options?.sortingOption?.value
          ? topLevelTasksInGroup.sort((a, b) => a.order - b.order)
          : sortTasks(topLevelTasksInGroup, options.sortingOption, clusterDueDateMap)

      const { flattenItems, tasksInGroup } = generateFlattenItemsForGroup({
        sortedTasks,
        subtasksByParentMap,
        group,
        options,
        clusterDueDateMap
      })

      tasksByGroupMap.set(groupId, tasksInGroup)

      if (options.spaceBetweenGroups) {
        flattenItems.push({
          id: SPACE_ITEM_ID,
          type: 'space',
          entity: null
        })
      }

      return items.concat(flattenItems)
    }, []) || []

  return { flattenItems, subtasksByParentMap, tasksByGroupMap }
}

/**
 * Generate items when there is no grouping
 * @param {Task[]} tasks
 * @param {ViewOptions} [options={}]
 * @returns {FlattenItemsData}
 */
function generateAllTasksItems(tasks: Task[], options: ViewOptions = {}): FlattenItemsData {
  const tasksByGroupMap = new Map<string, Task[]>()

  const { subtasksByParentMap, topLevelTasksByGroupMap, clusterDueDateMap } = getTasksMaps(tasks, () => [], options)

  const groupedTasks = topLevelTasksByGroupMap.get(UNGROUPED_TASKS_ID) || []
  const topLevelTasks = groupedTasks.map(groupedTask => groupedTask.task)
  const isSearching = !!options.searchString?.trim()

  const sortedTasks = options?.sortingOption?.value
    ? sortTasks(topLevelTasks, options.sortingOption, clusterDueDateMap)
    : topLevelTasks.sort(sortByCreatedOnFn)

  const { flattenItems, tasksInGroup } = generateFlattenItemsForGroup({
    sortedTasks,
    group: null,
    subtasksByParentMap,
    options,
    clusterDueDateMap
  })

  if (flattenItems.length) {
    if (options.allowTaskCreation && !isSearching) {
      flattenItems.unshift({
        id: ADD_TASK_ID,
        type: 'new',
        entity: null
      })
    }

    flattenItems.unshift({
      id: 'ALL_TASKS',
      groupId: 'ALL_TASKS',
      entity: options.filterName ? { id: 'ALL_TASKS', name: options.filterName } : null,
      type: 'group'
    })

    tasksByGroupMap.set('ALL_TASKS', tasksInGroup)
  }

  return { flattenItems, subtasksByParentMap, tasksByGroupMap }
}

/**
 * Returns maps of tasks grouped by their lists and subtasks grouped by their parent task
 * @param {Task[]} tasks
 * @param {(task: Task) => GroupingEntity[]} selectGroup
 * @param {ViewOptions} [options={}]
 * @param {Map<string, TaskListTimeline>} timelineListsMap Optional map for timeline list data
 * @param {boolean} forceClusterDueDateMap When true, forces building the clusterDueDateMap even if sortingOption is not dueOn
 * @returns {{ subtasksByParentMap: SubtasksByParentMap; topLevelTasksByGroupMap: Map<string, GroupedTask[]>; timelineListsMap?: Map<string, TaskListTimeline>; clusterDueDateMap?: ClusterDueDateMap; }}
 */
function getTasksMaps(
  tasks: Task[],
  selectGroup: (task: Task) => GroupingEntity[],
  options: ViewOptions = {},
  timelineListsMap?: Map<string, TaskListTimeline>,
  forceClusterDueDateMap?: boolean
): {
  subtasksByParentMap: SubtasksByParentMap
  topLevelTasksByGroupMap: Map<string, GroupedTask[]>
  timelineListsMap?: Map<string, TaskListTimeline>
  clusterDueDateMap?: ClusterDueDateMap
} {
  const subtasksByParentMap = new Map<string, { subtasks: Task[]; parent: Task }>()
  const topLevelTasksByGroupMap = new Map<string, GroupedTask[]>()
  const shouldBuildClusterDueDateMap = forceClusterDueDateMap || options?.sortingOption?.value === SortByOptions.dueOn
  const clusterDueDateMap: ClusterDueDateMap | undefined = shouldBuildClusterDueDateMap
    ? new Map<string, Date | null>()
    : undefined

  //Updates clusterDueDateMap with earliest due date. Subtasks must be NOT-CLOSED with dueOn.
  const updateClusterDueDate = (clusterId: string, task: Task | null | undefined, isSubtask: boolean): void => {
    if (!clusterDueDateMap || !clusterId || !task) {
      return
    }

    // Subtasks: only consider NOT-CLOSED tasks with a due date
    if (isSubtask && (task.isDoneAndNotJustCheckedOff || !task.dueOn)) {
      return
    }

    // Parent: consider dueOn whenever it exists, regardless of done state
    if (!isSubtask && !task.dueOn) {
      return
    }

    const current = clusterDueDateMap.get(clusterId) ?? null
    const candidate = task.dueOn

    if (!candidate) {
      return
    }

    if (!current || candidate.getTime() < current.getTime()) {
      clusterDueDateMap.set(clusterId, candidate)
    }
  }

  tasks.forEach(task => {
    const parentTask = task.parentId && tasks.find(t => t.id === task.parentId)
    const isOrphanSubtask = task.parentId && !parentTask

    if (timelineListsMap) {
      setTimelineListMapDates(task, timelineListsMap, selectGroup)
    }

    if (options?.hideOrphanSubtask && isOrphanSubtask) {
      return
    }

    if (!shouldIncludeTask(task, tasks, parentTask, options)) {
      return
    }

    const groups = selectGroup(task)

    // In case the task is a subtask, only add it to the subtasks map
    if (task.parentId) {
      if (!subtasksByParentMap.has(task.parentId)) {
        subtasksByParentMap.set(task.parentId, { subtasks: [], parent: parentTask })
      }

      subtasksByParentMap.get(task.parentId).subtasks.push(task)

      if (shouldBuildClusterDueDateMap) {
        // For orphan subtasks (parent not visible), use their own ID as cluster key for individual sorting
        const clusterKey = isOrphanSubtask ? task.id : task.parentId

        updateClusterDueDate(clusterKey, task, true)
      }

      if (!isOrphanSubtask) {
        return
      }
    } else {
      if (!subtasksByParentMap.has(task.id)) {
        subtasksByParentMap.set(task.id, { subtasks: [], parent: task })
      } else {
        subtasksByParentMap.get(task.id).parent = task
      }

      if (shouldBuildClusterDueDateMap) {
        // For top-level tasks, consider their due date regardless of done state
        updateClusterDueDate(task.id, task, false)
      }
    }

    if (groups?.length) {
      // In case is not a subtask, add it to each of its groups
      groups.forEach(group => {
        const groupId = group.id

        if (!topLevelTasksByGroupMap.has(groupId)) {
          topLevelTasksByGroupMap.set(groupId, [])
        }

        const groupedTask = { task, group }

        topLevelTasksByGroupMap.get(groupId).push(groupedTask)
      })
    } else {
      if (!topLevelTasksByGroupMap.has(UNGROUPED_TASKS_ID)) {
        topLevelTasksByGroupMap.set(UNGROUPED_TASKS_ID, [])
      }

      const groupedTask = { task, group: null }

      topLevelTasksByGroupMap.get(UNGROUPED_TASKS_ID).push(groupedTask)
    }
  })

  return { subtasksByParentMap, topLevelTasksByGroupMap, timelineListsMap, clusterDueDateMap }
}

/**
 * sets the start and end dates for the timeline lists based on the task's startOn and dueOn dates
 * @param {Task} task
 * @param {Map<string, TaskListTimeline>} timelineListsMap
 * @param {(task: Task) => GroupingEntity[]} selectGroup
 */
function setTimelineListMapDates(
  task: Task,
  timelineListsMap: Map<string, TaskListTimeline>,
  selectGroup: (task: Task) => GroupingEntity[]
): void {
  const lists = selectGroup(task)

  lists?.forEach(list => {
    let timelineList = timelineListsMap.get(list.id)

    if (!timelineList) {
      timelineList = new TaskListTimeline({ ...(list as TaskList), startOn: null, endOn: null })
      timelineListsMap.set(list.id, timelineList)
    }

    if (!task.startOn && !task.dueOn) {
      return
    }

    if (task.startOn && (!timelineList.startOn || isBefore(task.startOn, timelineList.startOn))) {
      timelineList.startOn = task.startOn
    }

    if (task.dueOn && (!timelineList.endOn || isAfter(task.dueOn, timelineList.endOn))) {
      timelineList.endOn = task.dueOn
    }
  })
}

/**
 * Generate flatten items for a group
 * @param {{ sortedTasks: Task[]; subtasksByParentMap: SubtasksByParentMap; group: GroupingEntity; options: ViewOptions; clusterDueDateMap?: ClusterDueDateMap; }} args
 * @returns {{ flattenItems: FlattenItem[]; tasksInGroup: Task[]; }}
 */
function generateFlattenItemsForGroup({
  sortedTasks,
  subtasksByParentMap,
  group,
  options,
  clusterDueDateMap
}: {
  sortedTasks: Task[]
  subtasksByParentMap: SubtasksByParentMap
  group: GroupingEntity
  options: ViewOptions
  clusterDueDateMap?: ClusterDueDateMap
}): { flattenItems: FlattenItem[]; tasksInGroup: Task[] } {
  const { isTaskCollapsed } = options

  const groupId = group?.id || UNGROUPED_TASKS_ID

  const items: FlattenItem[] = []
  const tasksInGroup: Task[] = []

  if (!sortedTasks.length) {
    return { flattenItems: [], tasksInGroup: [] }
  }

  const shouldUseClusterDueDate =
    !!clusterDueDateMap &&
    (options?.sortingOption?.value === SortByOptions.dueOn ||
      options?.groupOption === GroupByOptions.upcomingDaily ||
      options?.groupOption === GroupByOptions.upcomingWeekly ||
      options?.groupOption === GroupByOptions.upcomingMonthly)

  sortedTasks.forEach((task, index) => {
    let subtasks = subtasksByParentMap.get(task.id)?.subtasks || []

    subtasks = sortByAsc(subtasks, task => task.subtaskOrder)

    const hasVisibleSubtasks = subtasks?.length && !isTaskCollapsed?.(task.id)

    tasksInGroup.push(task, ...subtasks)

    // Calculate cluster due date, only shown when earlier than task's due date
    const rawClusterDueDate =
      shouldUseClusterDueDate && clusterDueDateMap ? getClusterDueDate(task, clusterDueDateMap) : null
    const shouldSetClusterDueDate =
      shouldUseClusterDueDate &&
      rawClusterDueDate != null &&
      (task.dueOn == null || rawClusterDueDate.getTime() < task.dueOn.getTime())
    const clusterDueDate = shouldSetClusterDueDate ? rawClusterDueDate : null
    const showDueToSubtask = shouldSetClusterDueDate

    items.push({
      id: task.id,
      entity: task,
      type: 'task',
      groupId: groupId,
      group: group,
      hasSubtasks: !!subtasks?.length,
      isFirstInGroup: index === 0,
      isLastInGroup: !hasVisibleSubtasks && index === sortedTasks.length - 1,
      showDueToSubtask,
      clusterDueDate
    })

    if (hasVisibleSubtasks) {
      subtasks.forEach((subtask, i) => {
        const isLastSubtask = i === subtasks.length - 1

        items.push({
          id: subtask.id,
          entity: subtask,
          type: 'subTask',
          groupId,
          group: group,
          parentTaskId: task.id,
          isFirstSubtask: i === 0,
          isLastSubtask: isLastSubtask,
          isLastInGroup: isLastSubtask && index === sortedTasks.length - 1
        })
      })
    }
  })

  return { flattenItems: items, tasksInGroup }
}

/**
 * Returns true if task can be included in the current filter options
 * @param {Task} task
 * @param {Task[]} allTasks
 * @param {Task} parentTask
 * @param {ViewOptions} [options={}]
 * @returns {boolean}
 */
function shouldIncludeTask(task: Task, allTasks: Task[], parentTask: Task, options: ViewOptions = {}): boolean {
  const { searchString, showScheduledTasks, showPlannedTasks } = options

  if (showPlannedTasks === false && task.isPlanned) {
    return false
  }

  if (showScheduledTasks === false && task.isCompletelyScheduled) {
    return false
  }

  if (!searchString?.trim()) {
    return isTaskStatusVisible(task, parentTask, options)
  }

  const taskContainsSearchString = isTaskContainingSearchString(task, options)
  let subtasksContainSearchString: boolean

  if (!taskContainsSearchString) {
    subtasksContainSearchString = allTasks.some(
      subtask => subtask.parentId === task.id && isTaskContainingSearchString(subtask, options)
    )
  }

  return (taskContainsSearchString || subtasksContainSearchString) && isTaskStatusVisible(task, parentTask, options)
}

/**
 * Returns whether a task contains the search string
 * @param {Task} task
 * @param {ViewOptions} [options={}]
 * @returns {boolean}
 */
function isTaskContainingSearchString(task: Task, options: ViewOptions = {}): boolean {
  const { searchString } = options

  return taskSearchFilter(task, searchString)
}

/**
 * Returns true if task status is visible in the current filter options
 * And in the case of a subtask, it returns true if the parent task is also visible
 * @param {Task} task
 * @param {Task} parentTask
 * @param {ViewOptions} [options={}]
 * @returns {boolean}
 */
function isTaskStatusVisible(task: Task, parentTask: Task, options: ViewOptions = {}): boolean {
  const { showRecentDoneTasks, showAllDoneTasks } = options

  if (showAllDoneTasks) {
    return true
  }

  if (showRecentDoneTasks) {
    return (
      (!task.isDoneAndNotJustCheckedOff && !parentTask?.isDoneAndNotJustCheckedOff) ||
      (!task.isOldDoneTask && !parentTask?.isOldDoneTask)
    )
  }

  return !task.isDoneAndNotJustCheckedOff && !parentTask?.isDoneAndNotJustCheckedOff
}

/**
 * Sorts groups by name
 * @param {GroupingEntity} groupA
 * @param {GroupingEntity} groupB
 * @returns {number}
 */
function sortGroupsByNameFn(groupA: GroupingEntity, groupB: GroupingEntity): number {
  const groupAName = groupA?.name
  const groupBName = groupB?.name

  if (!groupA) {
    return 1
  }

  if (!groupB) {
    return -1
  }

  return groupAName?.toLowerCase()?.localeCompare(groupBName?.toLowerCase())
}

/**
 * Calculates the cluster due date (earliest due date) for a task within its parent-subtask cluster.
 * The result is based solely on NOT-CLOSED tasks from the cluster; closed tasks are ignored.
 * @param {Task} task Task for which to get the cluster due date
 * @param {ClusterDueDateMap} clusterDueDateMap Precomputed map of cluster due dates
 * @returns {Date | null}
 */
function getClusterDueDate(task: Task, clusterDueDateMap: ClusterDueDateMap): Date | null {
  return clusterDueDateMap.get(task.id) ?? clusterDueDateMap.get(task.parentId) ?? null
}

/**
 * Sorts tasks by the selected option
 * @param {Task[]} tasks
 * @param {SortByOption} selectedOptions
 * @param {ClusterDueDateMap} clusterDueDateMap Optional map for cluster-based due date sorting
 * @returns {Task[]}
 */
function sortTasks(tasks: Task[], selectedOptions: SortByOption, clusterDueDateMap?: ClusterDueDateMap): Task[] {
  switch (selectedOptions?.value) {
    case SortByOptions.project:
      return [...tasks].sort((taskA, taskB) => compareTasksByProject(taskA, taskB, selectedOptions.orderBy))
    case SortByOptions.taskName:
      return selectedOptions.orderBy === 'asc' ? sortByAsc(tasks, t => t.name) : sortByDesc(tasks, t => t.name)
    case SortByOptions.typeOfWork:
      return selectedOptions.orderBy === 'asc'
        ? sortByAsc(tasks, t => t.typeOfWork.name)
        : sortByDesc(tasks, t => t.typeOfWork.name)
    case SortByOptions.startOn:
      return tasks.sort((taskA, taskB) =>
        compareDates(taskA.startOn, taskB.startOn, selectedOptions.orderBy === 'asc', true)
      )
    case SortByOptions.dueOn:
      // Use cluster-based sorting for due date
      if (clusterDueDateMap) {
        return tasks.sort((taskA, taskB) => {
          const clusterDueDateA = getClusterDueDate(taskA, clusterDueDateMap)
          const clusterDueDateB = getClusterDueDate(taskB, clusterDueDateMap)

          return compareDates(clusterDueDateA, clusterDueDateB, selectedOptions.orderBy === 'asc', true)
        })
      }

      return tasks.sort((taskA, taskB) =>
        compareDates(taskA.dueOn, taskB.dueOn, selectedOptions.orderBy === 'asc', true)
      )
    case SortByOptions.createdOn:
      return selectedOptions.orderBy === 'asc'
        ? sortByAsc(tasks, t => t.createdOn)
        : sortByDesc(tasks, t => t.createdOn)
    case SortByOptions.effort:
      return selectedOptions.orderBy === 'asc'
        ? sortByAsc(tasks, t => (t.plannedDuration ? t.plannedDuration : 0))
        : sortByDesc(tasks, t => (t.plannedDuration ? t.plannedDuration : 0))
    case SortByOptions.taskStatus:
      return selectedOptions.orderBy === 'asc'
        ? sortByAsc(tasks, t => t.getOrderableTaskStatusName())
        : sortByDesc(tasks, t => t.getOrderableTaskStatusName())
    case SortByOptions.user:
      return getSortedTasksByUser(tasks, selectedOptions.orderBy)
    default:
      return selectedOptions?.orderBy === 'asc'
        ? sortByAsc(tasks, t => t.createdOn)
        : sortByDesc(tasks, t => t.createdOn)
  }
}

/**
 * Compare two dates for sorting
 * @param {Date} dateA
 * @param {Date} dateB
 * @param {boolean} [asc=true]
 * @param {boolean} [showNullAtTheEnd=false] whether to show null dates at the end regardless of the order
 * @returns {number}
 */
function compareDates(dateA: Date, dateB: Date, asc = true, showNullAtTheEnd = false): number {
  const order = asc ? 1 : -1

  if (!dateA) {
    return showNullAtTheEnd ? 1 : -1 * order
  }

  if (!dateB) {
    return showNullAtTheEnd ? -1 : order
  }

  return (dateA.getTime() - dateB.getTime()) * order
}

/**
 * Returns a function that gets the project/projecttype group for a given task
 * @param {GroupByOptions} grouping
 * @returns {(task: Task) => Project[] | ProjectType[] | GenericGroup[]}
 */
function getProjectsGroupFn(grouping: GroupByOptions): (task: Task) => Project[] | ProjectType[] | GenericGroup[] {
  const privateGroup = { id: PRIVATE_TASKS_ID, name: PRIVATE_TASKS_ID }

  if (grouping === GroupByOptions.project) {
    return (task: Task) => (task.project ? [task.project] : [privateGroup])
  }

  return (task: Task) => {
    if (!task.project) {
      return [privateGroup]
    }

    return task.project.projectType ? [task.project.projectType] : []
  }
}

/**
 * Returns function in which gets the "Upcoming" group name for a given task
 * @param {GroupByOptions} grouping
 * @param {ClusterDueDateMap} clusterDueDateMap Optional map to compute cluster (parent+subtasks) earliest due date
 * @returns {(task: Task) => GenericGroup[]}
 */
function getUpcomingGroupFn(
  grouping: GroupByOptions,
  clusterDueDateMap?: ClusterDueDateMap
): (task: Task) => GenericGroup[] {
  const endOfTod = endOfToday().toISOString()
  const endOfYest = endOfYesterday().toISOString()
  const endOfTom = endOfTomorrow().toISOString()
  const next7 = addDays(new Date(), 7).toISOString()
  const next30 = addDays(new Date(), 30).toISOString()

  const endOfThisWeek = endOfWeek(new Date()).toISOString()
  const endOfLastWeek = addDays(endOfThisWeek, -7).toISOString()
  const nextWeek = addWeeks(endOfWeek(new Date()), 1).toISOString()

  const currentMonth = endOfMonth(new Date()).toISOString()
  const lastMonth = endOfMonth(addMonths(new Date(), -1)).toISOString()
  const nextMonth = endOfMonth(addMonths(new Date(), 1)).toISOString()

  switch (grouping) {
    case GroupByOptions.upcomingDaily:
      return (task: Task) => {
        // Determine effective due date based on cluster (parent + subtasks)
        const effectiveDue = clusterDueDateMap ? getClusterDueDate(task, clusterDueDateMap) : task.dueOn

        const endOfTodayDate = new Date(endOfTod)
        const endOfYesterdayDate = new Date(endOfYest)
        const endOfTomorrowDate = new Date(endOfTom)
        const next7Date = new Date(next7)
        const next30Date = new Date(next30)

        if (effectiveDue) {
          // Recently closed: older than yesterday and done
          if (effectiveDue.getTime() < endOfYesterdayDate.getTime() && task.isDoneAndNotJustCheckedOff) {
            return [{ id: UpcomingGroup.RECENTLY_CLOSED, name: UpcomingGroup.RECENTLY_CLOSED }]
          }

          if (effectiveDue.getTime() <= endOfTodayDate.getTime()) {
            return [{ id: UpcomingGroup.TODAY, name: UpcomingGroup.TODAY }]
          }

          if (effectiveDue.getTime() <= endOfTomorrowDate.getTime()) {
            return [{ id: UpcomingGroup.TOMORROW, name: UpcomingGroup.TOMORROW }]
          }

          if (effectiveDue.getTime() <= next7Date.getTime()) {
            return [{ id: UpcomingGroup.NEXT_7_DAYS, name: UpcomingGroup.NEXT_7_DAYS }]
          }

          if (effectiveDue.getTime() <= next30Date.getTime()) {
            return [{ id: UpcomingGroup.NEXT_30_DAYS, name: UpcomingGroup.NEXT_30_DAYS }]
          }

          return [{ id: UpcomingGroup.UPCOMING, name: UpcomingGroup.UPCOMING }]
        }

        return [{ id: UpcomingGroup.UNSCHEDULED, name: UpcomingGroup.UNSCHEDULED }]
      }
    case GroupByOptions.upcomingWeekly:
      return (task: Task) => {
        const effectiveDue = clusterDueDateMap ? getClusterDueDate(task, clusterDueDateMap) : task.dueOn

        const endOfThisWeekDate = new Date(endOfThisWeek)
        const endOfLastWeekDate = new Date(endOfLastWeek)
        const nextWeekDate = new Date(nextWeek)

        if (effectiveDue) {
          if (effectiveDue.getTime() < endOfLastWeekDate.getTime() && task.isDoneAndNotJustCheckedOff) {
            return [{ id: UpcomingGroup.RECENTLY_CLOSED, name: UpcomingGroup.RECENTLY_CLOSED }]
          }

          if (effectiveDue.getTime() <= endOfThisWeekDate.getTime()) {
            return [{ id: UpcomingGroup.THIS_WEEK, name: UpcomingGroup.THIS_WEEK }]
          }

          if (effectiveDue.getTime() <= nextWeekDate.getTime()) {
            return [{ id: UpcomingGroup.NEXT_WEEK, name: UpcomingGroup.NEXT_WEEK }]
          }

          return [{ id: UpcomingGroup.UPCOMING, name: UpcomingGroup.UPCOMING }]
        }

        return [{ id: UpcomingGroup.UNSCHEDULED, name: UpcomingGroup.UNSCHEDULED }]
      }
    case GroupByOptions.upcomingMonthly:
      return (task: Task) => {
        const effectiveDue = clusterDueDateMap ? getClusterDueDate(task, clusterDueDateMap) : task.dueOn

        const currentMonthDate = new Date(currentMonth)
        const lastMonthDate = new Date(lastMonth)
        const nextMonthDate = new Date(nextMonth)

        if (effectiveDue) {
          if (effectiveDue.getTime() < lastMonthDate.getTime() && task.isDoneAndNotJustCheckedOff) {
            return [{ id: UpcomingGroup.RECENTLY_CLOSED, name: UpcomingGroup.RECENTLY_CLOSED }]
          }

          if (effectiveDue.getTime() <= currentMonthDate.getTime()) {
            return [{ id: UpcomingGroup.THIS_MONTH, name: UpcomingGroup.THIS_MONTH }]
          }

          if (effectiveDue.getTime() <= nextMonthDate.getTime()) {
            return [{ id: UpcomingGroup.NEXT_MONTH, name: UpcomingGroup.NEXT_MONTH }]
          }

          return [{ id: UpcomingGroup.UPCOMING, name: UpcomingGroup.UPCOMING }]
        }

        return [{ id: UpcomingGroup.UNSCHEDULED, name: UpcomingGroup.UNSCHEDULED }]
      }

    default:
      return () => []
  }
}

/**
 * Returns the order of a task item based on the position of the item in the list
 * @param {FlattenItem[]} taskItems
 * @param {number} position
 * @returns {number}
 */
export function getFlattenItemOrder(taskItems: FlattenItem[], position: number): number {
  if (!taskItems?.length) {
    return 0
  }

  if (position === 0) {
    return (taskItems[0].group as TaskList).orderOfTask - 1
  }

  let order = (taskItems[taskItems.length - 1]?.group as TaskList).orderOfTask + 1

  const taskItemBefore = taskItems[position - 1]

  if (taskItemBefore) {
    const taskAtPosition = taskItems[position]
    const taskAtPositionList = taskAtPosition?.group as TaskList
    const taskItemBeforeList = taskItemBefore?.group as TaskList

    if (taskAtPosition) {
      order = (taskItemBeforeList.orderOfTask + taskAtPositionList.orderOfTask) / 2
    } else {
      order = taskItemBeforeList.orderOfTask + 1
    }
  }

  return order
}

/**
 * Sort Function by task created on moving undefined to the end
 * @param {Task} taskA
 * @param {Task} taskB
 * @returns {number}
 */
function sortByCreatedOnFn(taskA: Task, taskB: Task): number {
  const createdOnA = new Date(taskA.createdOn)
  const createdOnB = new Date(taskB.createdOn)

  if (!createdOnA && !createdOnB) {
    return 0
  } else if (!createdOnA) {
    return 1
  } else if (!createdOnB) {
    return -1
  }

  return createdOnA.getTime() - createdOnB.getTime()
}

/**
 * Gets the generic group name for a list according to the type of grouping
 * @param {GroupingEntity} entity
 * @param {string} title
 * @param {{ groupOption?: GroupByOptions; isPrivateTaskGroup?: boolean; }} options
 * @returns {string}
 */
export function getGroupName(
  entity: GroupingEntity,
  title: string,
  options?: {
    groupOption?: GroupByOptions
    isPrivateTaskGroup?: boolean
  }
): string {
  const { groupOption, isPrivateTaskGroup } = options || {}
  const isSearchGroup = entity?.id === SEARCH_RESULTS_ID
  const isWithoutGroup = !entity || entity.id === UNGROUPED_TASKS_ID

  switch (groupOption) {
    case GroupByOptions.list:
      return entity?.id === WITHOUT_LIST_ID ? q.translations.common.withoutList : title
    case GroupByOptions.typeOfWork:
      return title
    case GroupByOptions.project:
    case GroupByOptions.projectType:
      if (isPrivateTaskGroup) {
        return q.translations.FlatItems.privateTaskListTitle
      }

      return isWithoutGroup ? q.translations.FlatItems.withoutProjectType : title
    case GroupByOptions.upcomingDaily:
    case GroupByOptions.upcomingMonthly:
    case GroupByOptions.upcomingWeekly:
      return getUpcomingGroupHeaderName(entity)
    case GroupByOptions.user:
      return isWithoutGroup ? q.translations.FlatItems.withoutUser : title
    case GroupByOptions.client:
      return isWithoutGroup ? q.translations.FlatItems.withoutClient : title
    case GroupByOptions.none:
      return isWithoutGroup ? q.translations.FlatItems.allTasks : title
    case GroupByOptions.taskLists:
      if (entity?.id === WITHOUT_LIST_ID) {
        return q.translations.common.withoutList
      }

      if (isSearchGroup) {
        return q.translations.FlatItems.searchResults
      }

      return title
    default:
      return title
  }
}

/**
 * Returns the name of the upcoming group header
 * @param {GroupingEntity | null} group
 * @returns {string}
 */
export function getUpcomingGroupHeaderName(group: GroupingEntity | null): string {
  switch (group?.id) {
    case UpcomingGroup.TODAY:
      return q.translations.FlatItems.today
    case UpcomingGroup.TOMORROW:
      return q.translations.FlatItems.tomorrow
    case UpcomingGroup.THIS_WEEK:
      return q.translations.FlatItems.thisWeek
    case UpcomingGroup.NEXT_WEEK:
      return q.translations.FlatItems.nextWeek
    case UpcomingGroup.NEXT_MONTH:
      return q.translations.FlatItems.nextMonth
    case UpcomingGroup.THIS_MONTH:
      return q.translations.FlatItems.thisMonth
    case UpcomingGroup.NEXT_7_DAYS:
      return q.translations.FlatItems.next7Days
    case UpcomingGroup.NEXT_30_DAYS:
      return q.translations.FlatItems.next30Days
    case UpcomingGroup.RECENTLY_CLOSED:
      return q.translations.FlatItems.recentlyClosed
    case UpcomingGroup.UPCOMING:
      return q.translations.FlatItems.upcoming
    case UpcomingGroup.UNSCHEDULED:
    default:
      return q.translations.FlatItems.Unscheduled
  }
}

/**
 * Casts the flatten item to a task
 * @param {Task | GroupingEntity} flattenItem
 * @returns {Task}
 */
export function getTask(flattenItem: Task | GroupingEntity): Task {
  return flattenItem as Task
}

/**
 * Casts the flatten item group to a task list
 * @param {GroupingEntity} flattenItemGroup
 * @returns {TaskList}
 */
export function getTaskList(flattenItemGroup: GroupingEntity): TaskList {
  return flattenItemGroup as TaskList
}
